import {
  SupportTicketListItemResponse,
  type SupportTicketListItemResponse as SupportTicketListItemResponseType,
  type UpdateSupportTicketInput,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { SupportTicketListQueryParams } from "@redotech/effect-api-shared/rpc/support-ticket";
import { Option } from "effect";

export const DEFAULT_SUPPORT_TICKET_PAGE_SIZE = 50;

/** Number of pages to prefetch ahead of the current page. */
export const SUPPORT_TICKET_PREFETCH_PAGES_AHEAD = 3;

export type SupportTicketListUrlParams = SupportTicketListQueryParams;

export type SupportTicketListFetchOptions = Pick<
  SupportTicketListUrlParams,
  "includeHighlights" | "includeTotalCount" | "searchAfter"
>;

function toOptionIfPresent<T>(
  value: T | null | undefined,
): Option.Option<T> | undefined {
  if (value === undefined) {
    return undefined;
  }
  return value === null ? Option.none() : Option.some(value);
}

export function buildSupportTicketListUrlParams(
  page: number,
  filterGroup: string | undefined,
  pageSize = DEFAULT_SUPPORT_TICKET_PAGE_SIZE,
  sorts?: string,
  options: SupportTicketListFetchOptions = {},
): SupportTicketListUrlParams {
  return {
    limit: pageSize,
    offset: page * pageSize,
    ...(options.searchAfter && options.searchAfter.length > 0
      ? { searchAfter: options.searchAfter }
      : {}),
    filterGroup,
    sorts,
    ...(options.includeTotalCount === undefined
      ? {}
      : { includeTotalCount: options.includeTotalCount }),
    ...(options.includeHighlights === undefined
      ? {}
      : { includeHighlights: options.includeHighlights }),
  };
}

export function toUpdateSupportTicketInput(
  changes: Partial<SupportTicketListItemResponseType>,
): UpdateSupportTicketInput {
  return {
    status: changes.status,
    priority: toOptionIfPresent(changes.priority),
    readAt: toOptionIfPresent(changes.readAt),
    mongoAssigneeId: toOptionIfPresent(changes.mongoAssigneeId),
    assigneeType:
      changes.mongoAssigneeId === undefined
        ? undefined
        : changes.mongoAssigneeId === null
          ? Option.none()
          : Option.some("USER"),
    mongoCustomerId: toOptionIfPresent(changes.mongoCustomerId),
    tagUpdate: changes.tagIds
      ? { _tag: "replace", tagIds: changes.tagIds }
      : undefined,
    snoozedUntil: changes.snoozedUntil,
  };
}

export function serializeSupportTicket(
  supportTicket: SupportTicketListItemResponseType,
): typeof SupportTicketListItemResponse.Encoded {
  return {
    ...supportTicket,
    readAt: supportTicket.readAt?.toISOString() ?? null,
    lastCustomerMessageAt:
      supportTicket.lastCustomerMessageAt?.toISOString() ?? null,
    lastMerchantMessageAt:
      supportTicket.lastMerchantMessageAt?.toISOString() ?? null,
    createdAt: supportTicket.createdAt?.toISOString() ?? null,
    statusUpdatedAt: supportTicket.statusUpdatedAt?.toISOString() ?? null,
    snoozedUntil: supportTicket.snoozedUntil?.toISOString() ?? null,
  };
}
